import {
  createDriver,
  EventNormalizer,
  resolveModelTier,
  type AgentConfig,
  type AgentDriver,
  type AgentEvent,
  type ModelTierOverrides,
} from "@skaile/workspaces/bridge";
import type { BindingOrigin } from "./contract/expression.js";
import type { FlowNode } from "./engine/types.js";

/** Strict v2 subprompt node admitted by the pure engine. */
export type SubpromptFlowNode = FlowNode & {
  run: Extract<NonNullable<FlowNode["run"]>, { kind: "subprompt" }>;
};

/** Resolved execution request for one isolated subprompt call. */
export interface SubpromptExecutorRequest {
  /** Available strict v2 subprompt node selected by the pure engine. */
  node: SubpromptFlowNode;
  /**
   * Fully-assembled prompt text — the node's instruction, already interpolated
   * with resolved bindings, plus a JSON-response directive when the node
   * declares an output contract. This is the ONLY input the isolated driver
   * sees: no session conversation, no driver context.
   */
  prompt: string;
  /** Binding values already resolved at the node boundary — carried for parity with the other executor requests. */
  inputs: Record<string, unknown>;
  /** Provenance retained separately from the values sent to the model. */
  inputOrigins: Record<string, BindingOrigin[]>;
  /** Cancels the owned driver and its in-flight tool calls when the node stops waiting. */
  signal?: AbortSignal;
}

/** Captured result of one isolated subprompt driver call. */
export interface SubpromptExecutorResult {
  /** Text scraped from the isolated driver's response, accumulated across its stream. */
  text: string;
}

/** Runtime seam for the subprompt node kind — a runtime-owned, isolated model call. */
export interface SubpromptRunner {
  /** Executes one available subprompt node on a fresh, isolated driver instance. */
  execute(request: SubpromptExecutorRequest): Promise<SubpromptExecutorResult>;
}

/**
 * Non-secret cloud transport settings, field-identical to bridge's
 * `AgentConfig.cloudConfig` — kept inline rather than imported so this file's
 * public option surface doesn't widen with every unrelated `AgentConfig` field.
 */
export interface SubpromptCloudConfig {
  region?: string;
  projectId?: string;
  resource?: string;
  baseUrl?: string;
}

/** Host dependencies for constructing one isolated driver per subprompt call. */
export interface SessionSubpromptRunnerOptions {
  /** Driver backend id — the same one the session driver was constructed with (e.g. `"claude-sdk"`, `"omp"`). */
  driverType: string;
  /** LLM provider — the same value the session driver was constructed with. */
  provider?: string;
  /** Concrete model id the session driver is using; the fallback for every tier absent a configured override. */
  sessionModel?: string;
  /** Working directory for the isolated driver — the already-materialized session workspace. */
  workspaceDir: string;
  /** Pre-resolved API key forwarded to every isolated instance — the same value the session driver received, never re-resolved here. */
  apiKey?: string;
  /** Cloud transport — the same value the session driver was constructed with. Omit (or `"default"`) for the provider's native API. */
  cloud?: string;
  cloudConfig?: SubpromptCloudConfig;
  cloudSecrets?: Record<string, string>;
  /** Per-tier model overrides for this deployment. Absent means the provider's built-in tiers apply. */
  modelTierOverrides?: ModelTierOverrides;
  /** Authoritative managed-model inventory; a missing requested model is denied before launch. */
  availableModels?: readonly string[];
  /**
   * Hard ceiling on one isolated call, defaulting to
   * {@link DEFAULT_SUBPROMPT_TIMEOUT_MS}. Independent of `control.timeoutSec`,
   * which is optional and bounds the adapter's *wait* without reaching the
   * driver this class owns.
   */
  timeoutMs?: number;
  /** Runtime-owned construction seam for managed drivers that need fresh private state per call. */
  createIsolatedDriver?: (model: string | undefined) => Promise<{
    driver: AgentDriver;
    dispose?: () => void | Promise<void>;
  }>;
}

/**
 * Runs subprompt nodes on isolated `AgentDriver` instances constructed the
 * same way the session driver was — same backend, credentials, and cloud
 * transport — but with no system prompt, no tools, and no conversation
 * history. Each call spins a fresh instance and kills it afterward;
 * isolation comes from the separate instance, not from clearing shared state
 * (`resetSession()` mutates the shared session and is never used here).
 *
 * Credentials reach this class exactly the way they reach the session
 * driver: the two production wiring sites (`run-flow.ts`, `runner/src/serve.ts`)
 * pass through the same resolved `provider`/`apiKey`/`cloud*` values they
 * already computed for `createSessionDriver` — no new resolution path.
 */
export class SessionSubpromptRunner implements SubpromptRunner {
  constructor(private readonly options: SessionSubpromptRunnerOptions) {}

  async execute(request: SubpromptExecutorRequest): Promise<SubpromptExecutorResult> {
    // Throws on a `small`/`deep` this provider cannot serve, which surfaces as an
    // ordinary node failure — deliberately louder than running an arbitrary model
    // behind an author's explicit cost or quality request.
    const model = resolveModelTier(request.node.run.model, this.options.sessionModel, {
      provider: this.options.provider,
      cloud: this.options.cloud,
      overrides: this.options.modelTierOverrides,
    });
    if (model && this.options.availableModels && !this.options.availableModels.includes(model))
      throw new Error("CODEX_MODEL_UNAVAILABLE");
    const config: AgentConfig = {
      cwd: this.options.workspaceDir,
      provider: this.options.provider,
      model,
      // A subprompt is one model call by definition. Backends default to a
      // multi-turn agentic loop (claude-sdk: 15), which would let a node that
      // was asked to classify a string go off and work instead.
      maxTurns: 1,
      ...(this.options.apiKey
        ? {
            apiKeys: {
              [this.options.provider ?? "anthropic"]: this.options.apiKey,
            },
          }
        : {}),
      ...(this.options.cloud && this.options.cloud !== "default"
        ? { cloud: this.options.cloud }
        : {}),
      ...(this.options.cloudConfig ? { cloudConfig: this.options.cloudConfig } : {}),
      ...(this.options.cloudSecrets ? { cloudSecrets: this.options.cloudSecrets } : {}),
    };
    if (request.signal?.aborted) throw new Error("isolated subprompt call cancelled");
    const timeoutMs = this.options.timeoutMs ?? DEFAULT_SUBPROMPT_TIMEOUT_MS;
    let owned: { driver: AgentDriver; dispose?: () => void | Promise<void> } | undefined;
    let cleanupPromise: Promise<void> | undefined;
    const cleanup = async (abort: boolean) => {
      if (!owned) return;
      cleanupPromise ??= (async () => {
        if (abort) await owned!.driver.abort().catch(() => {});
        await owned!.driver.dispose();
        await owned!.dispose?.();
      })();
      await cleanupPromise;
    };
    let rejectStopped!: (error: Error) => void;
    let shouldAbort = false;
    const stopped = new Promise<never>((_, reject) => {
      rejectStopped = reject;
    });
    const stop = (error: Error) => {
      shouldAbort = true;
      rejectStopped(error);
      void cleanup(true).catch(() => {});
    };
    const cancelled = () => stop(new Error("isolated subprompt call cancelled"));
    const timer = setTimeout(
      () => stop(new Error(`isolated subprompt call exceeded ${timeoutMs}ms`)),
      timeoutMs,
    );
    if (request.signal?.aborted) cancelled();
    else request.signal?.addEventListener("abort", cancelled, { once: true });
    const construction = this.options.createIsolatedDriver
      ? this.options.createIsolatedDriver(model)
      : createDriver(this.options.driverType, config).then((driver) => ({
          driver,
        }));
    try {
      owned = await Promise.race([construction, stopped]);
      const starting = owned.driver.start();
      void starting.catch(() => {});
      await Promise.race([starting, stopped]);
      const prompting = runIsolatedPrompt(owned.driver, request.prompt);
      void prompting.catch(() => {});
      const text = await Promise.race([prompting, stopped]);
      return { text };
    } finally {
      clearTimeout(timer);
      request.signal?.removeEventListener("abort", cancelled);
      if (!owned) {
        // Construction may finish after this call has already been cancelled.
        // Dispose that late child without starting it or touching parent state.
        void construction
          .then((late) => {
            owned = late;
            return cleanup(true);
          })
          .catch(() => {});
      } else await cleanup(shouldAbort);
    }
  }
}

/**
 * Ceiling on one isolated subprompt call. Every other model call in the runtime
 * is bounded (`turnTimeoutMs`, `FLOW_TURN_TIMEOUT_MS`); `control.timeoutSec` is
 * optional and has no default, so without this a backend that never emits
 * `agent_end` — one parked on a tool that awaits a human, say — would hang the
 * flow's drain permanently rather than failing the node.
 */
export const DEFAULT_SUBPROMPT_TIMEOUT_MS = 5 * 60_000;

/**
 * Send one prompt to a freshly constructed driver and resolve with the text
 * accumulated from its response.
 *
 * `AgentDriver.prompt()` resolves to `void` and emits output only as stream
 * events, so the completion signal and the text both come from the listener,
 * not from `prompt()`'s own promise — mirroring `awaitAgentEnd` in
 * `run-flow.ts` (wait for `agent_end`/`error`, treat `prompt()`'s rejection
 * only as a safety net). Text is accumulated via `EventNormalizer`, the same
 * mechanism the runner uses to turn raw per-driver event shapes (block arrays
 * for claude-sdk, plain-string `message.content` for the echo driver, streamed
 * `message_update` deltas) into normalized `{ type: "text" }` events — hand-rolling
 * a narrower parser here would silently drop text on any driver whose raw shape
 * that parser didn't anticipate.
 */
function runIsolatedPrompt(driver: AgentDriver, prompt: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const normalizer = new EventNormalizer();
    let text = "";
    let settled = false;

    const done = (err?: Error) => {
      if (settled) return;
      settled = true;
      driver.off("agent-event", onEvent);
      if (err) reject(err);
      else resolve(text);
    };

    const onEvent = (event: AgentEvent) => {
      for (const normalized of normalizer.normalize(event)) {
        if (normalized.type === "text") text += normalized.content;
      }
      if (event.type === "agent_end") {
        done();
      } else if (event.type === "error") {
        done(new Error(event.error));
      }
    };

    driver.on("agent-event", onEvent);
    driver.prompt(prompt).catch(done);
  });
}
